import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { type DecisionQuestionOrigin, type DecisionSheetStatus } from '@apm/contracts';
import { cn } from '../../../lib/utils';

/**
 * Nhãn của sổ — bảng lớp **viết sẵn** (bẫy Tailwind ghép lớp lúc chạy ở
 * CLAUDE.md: lớp ghép từ chuỗi không được sinh, nhãn hiện đúng chữ mà trong suốt).
 */

export const PILL =
  'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium';

const STATUS_CLASS: Record<DecisionSheetStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  open: 'bg-rag-green/15 text-rag-green',
  closed: 'bg-foreground/10 text-foreground',
};

export function SheetStatusPill({ status }: { readonly status: DecisionSheetStatus }): ReactNode {
  const { t } = useTranslation();
  return (
    <span className={cn(PILL, STATUS_CLASS[status])}>{t(`decisionSheet.status.${status}`)}</span>
  );
}

export const ORIGIN_CLASS: Record<DecisionQuestionOrigin, string> = {
  documented: 'bg-tag-1/15 text-tag-1',
  new: 'bg-tag-4/15 text-tag-4',
};

export function OriginPill({ origin }: { readonly origin: DecisionQuestionOrigin }): ReactNode {
  const { t } = useTranslation();
  return (
    <span
      className={cn(PILL, ORIGIN_CLASS[origin])}
      title={t(`decisionSheet.originHint.${origin}`)}
    >
      {t(`decisionSheet.origin.${origin}`)}
    </span>
  );
}

export function AnsweredPill({ answered }: { readonly answered: boolean }): ReactNode {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        PILL,
        answered ? 'bg-primary/15 text-primary' : 'bg-rag-amber/15 text-rag-amber',
      )}
    >
      {answered
        ? t('decisionSheet.questionStatus.answered')
        : t('decisionSheet.questionStatus.open')}
    </span>
  );
}

export function RecommendedPill(): ReactNode {
  const { t } = useTranslation();
  return (
    <span
      className={cn(PILL, 'bg-rag-green/15 text-rag-green')}
      title={t('decisionSheet.recommendedHint')}
    >
      {t('decisionSheet.recommended')}
    </span>
  );
}

export function HighlightPill({ children }: { readonly children: ReactNode }): ReactNode {
  return <span className={cn(PILL, 'bg-rag-red/15 text-rag-red')}>{children}</span>;
}
